import React, { useState } from 'react';

// SVG Icons
const PasteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ImportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const ClearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

/**
 * TrendlineStrategyImporter - Component to import strategy JSON and populate the form
 * 
 * @param {Object} props
 * @param {Function} props.onImport - Callback when strategy is imported (strategy) => void
 */
const TrendlineStrategyImporter = ({ onImport }) => {
  const [open, setOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  const handleInputChange = (event) => {
    setJsonInput(event.target.value);
    setError(null);
  };

  const handleClear = () => {
    setJsonInput('');
    setError(null);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setJsonInput(text);
      setError(null);
    } catch (err) {
      setError('Failed to paste from clipboard. Please paste manually.');
    }
  };

  // Helper to format date to YYYY-MM-DD for input[type=date]
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    // If already in YYYY-MM-DD format
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    // If timestamp (number)
    if (typeof dateValue === 'number') {
      const date = new Date(dateValue);
      return date.toISOString().split('T')[0];
    }
    // If Date object or parseable string
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      // ignore
    }
    return '';
  };

  const validateAndTransformStrategy = (parsed) => {
    // Validate required fields for trendline strategy
    if (!parsed.name) {
      throw new Error('Missing required field: name');
    }

    // Transform to the expected format
    const strategy = {
      name: parsed.name || '',
      description: parsed.description || '',
      tickerName: parsed.tickerName || parsed.ticker || parsed.symbol || '',
      startDate: formatDateForInput(parsed.startDate),
      endDate: formatDateForInput(parsed.endDate),
      interval: parsed.interval || '1h',
      capitalAmount: parsed.capitalAmount || null,
      lotSize: parsed.lotSize != null ? parseInt(parsed.lotSize, 10) : null,
      tradingType: parsed.tradingType || 'INTRADAY',
      orderType: parsed.orderType || 'MARKET_ORDER',
      riskManagement: parsed.riskManagement || null,
      ruleGroups: [],
    };

    const mapCondition = (condition, condIndex) => {
      if (condition.conditionType === 'TRENDLINE') {
        return {
          conditionId: condition.conditionId || `cond-${Date.now()}-${condIndex}`,
          conditionType: 'TRENDLINE',
          triggerType: condition.triggerType || 'PRICE_TOUCHED_TREND_LINE',
          trendLine: {
            id: condition.trendLine?.id || `tl-${Date.now()}-${condIndex}`,
            name: condition.trendLine?.name || '',
            x1: condition.trendLine?.x1 || null,
            y1: condition.trendLine?.y1 || null,
            x2: condition.trendLine?.x2 || null,
            y2: condition.trendLine?.y2 || null,
          },
        };
      } else if (condition.conditionType === 'INDICATOR') {
        return {
          conditionId: condition.conditionId || `cond-${Date.now()}-${condIndex}`,
          conditionType: 'INDICATOR',
          indicatorType: condition.indicatorType || '',
          operator: condition.operator || 'LESS_THAN',
          parameters: condition.parameters || {},
        };
      } else if (condition.conditionType === 'COMPOSITE') {
        return {
          conditionId: condition.conditionId || `cond-${Date.now()}-${condIndex}`,
          conditionType: 'COMPOSITE',
          operator: condition.operator || 'CROSSED_UP',
          leftIndicator: condition.leftIndicator || { type: '', parameters: {} },
          rightIndicator: condition.rightIndicator || { type: '', parameters: {} },
        };
      } else if (condition.conditionType === 'VOTE') {
        return {
          conditionId: condition.conditionId || `cond-${Date.now()}-${condIndex}`,
          conditionType: 'VOTE',
          requiredVotes: condition.requiredVotes || condition.parameters?.minVotes || 1,
          conditions: Array.isArray(condition.conditions) ? condition.conditions.map((c, i) => mapCondition(c, `${condIndex}-${i}`)) : [],
        };
      } else if (condition.conditionType === 'CHAIN') {
        return {
          conditionId: condition.conditionId || `cond-${Date.now()}-${condIndex}`,
          conditionType: 'CHAIN',
          initialCondition: condition.initialCondition ? mapCondition(condition.initialCondition, `${condIndex}-init`) : null,
          chainLinks: Array.isArray(condition.chainLinks) ? condition.chainLinks.map((link, i) => ({
            ...link,
            condition: link.condition ? mapCondition(link.condition, `${condIndex}-link-${i}`) : null,
            threshold: link.threshold || 1
          })) : [],
        };
      }
      return condition;
    };

    // Handle ruleGroups if present
    if (parsed.ruleGroups && Array.isArray(parsed.ruleGroups)) {
      strategy.ruleGroups = parsed.ruleGroups.map((group, groupIndex) => ({
        groupId: group.groupId || `group-${Date.now()}-${groupIndex}`,
        groupName: group.groupName || `Rule Group ${groupIndex + 1}`,
        operator: group.operator || 'AND',
        action: group.action || (group.groupName?.toLowerCase().includes('exit') ? 'SELL' : 'BUY'),
        conditions: (group.conditions || []).map(mapCondition),
      }));
    }

    // Handle legacy format with rules array
    if (parsed.rules && Array.isArray(parsed.rules) && strategy.ruleGroups.length === 0) {
      strategy.ruleGroups = [{
        groupId: `group-${Date.now()}`,
        groupName: 'Imported Rules',
        operator: 'AND',
        action: 'BUY',
        conditions: parsed.rules.map((rule, index) => ({
          conditionId: `cond-${Date.now()}-${index}`,
          conditionType: rule.type === 'trendline' ? 'TRENDLINE' : 'INDICATOR',
          ...(rule.type === 'trendline' ? {
            triggerType: rule.triggerType || 'PRICE_TOUCHED_TREND_LINE',
            trendLine: rule.trendLine || {},
          } : {
            indicatorType: rule.indicator || rule.indicatorType || '',
            operator: rule.operator || 'LESS_THAN',
            parameters: rule.parameters || {},
          }),
        })),
      }];
    }

    // Handle entryRules and exitRules format
    if (parsed.entryRules && Array.isArray(parsed.entryRules)) {
      strategy.ruleGroups.push({
        groupId: `group-entry-${Date.now()}`,
        groupName: 'Entry Rules',
        operator: 'AND',
        action: 'BUY',
        conditions: parsed.entryRules.map(mapCondition),
      });
    }

    if (parsed.exitRules && Array.isArray(parsed.exitRules)) {
      strategy.ruleGroups.push({
        groupId: `group-exit-${Date.now()}`,
        groupName: 'Exit Rules',
        operator: 'AND',
        action: 'SELL',
        conditions: parsed.exitRules.map(mapCondition),
      });
    }

    return strategy;
  };

  const handleImport = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      const strategy = validateAndTransformStrategy(parsed);

      onImport(strategy);
      setOpen(false);
      setJsonInput('');
      setError(null);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format. Please check your input.');
      } else {
        setError(err.message);
      }
    }
  };

  // Example strategy JSON for reference
  const exampleJson = `{
  "name": "My Trendline Strategy",
  "description": "Buy when price touches support, sell when overbought",
  "tickerName": "RELIANCE",
  "startDate": "2024-01-01",
  "endDate": "2024-12-31",
  "lotSize": 5,
  "ruleGroups": [
    {
      "groupName": "Entry Rules",
      "action": "BUY",
      "operator": "AND",
      "conditions": [
        {
          "conditionType": "TRENDLINE",
          "triggerType": "PRICE_TOUCHED_TREND_LINE",
          "trendLine": {
            "name": "Support Line",
            "x1": 1704067200000,
            "y1": 2400,
            "x2": 1735603200000,
            "y2": 2600
          }
        },
        {
          "conditionType": "INDICATOR",
          "indicatorType": "RSI",
          "operator": "LESS_THAN",
          "parameters": {
            "period": 14,
            "value": 30
          }
        }
      ]
    },
    {
      "groupName": "Exit Rules",
      "action": "SELL",
      "operator": "OR",
      "conditions": [
        {
          "conditionType": "INDICATOR",
          "indicatorType": "RSI",
          "operator": "GREATER_THAN",
          "parameters": {
            "period": 14,
            "value": 70
          }
        }
      ]
    }
  ]
}`;

  return (
    <div>
      {/* Collapsible Import Section */}
      <div className="collapse collapse-arrow border border-base-300 bg-base-100 rounded-lg mb-4">
        <input type="checkbox" checked={expanded} onChange={() => setExpanded(!expanded)} />
        <div className="collapse-title flex items-center gap-2">
          <span className="text-primary"><ImportIcon /></span>
          <span className="font-medium text-sm">Import Strategy from JSON</span>
        </div>
        <div className="collapse-content">
          <p className="text-sm text-base-content/60 mb-4">
            Paste a strategy JSON to populate the form below. Click the button to open the import dialog.
          </p>
          <button className="btn btn-outline gap-1" onClick={handleOpen}>
            <PasteIcon />
            Paste Strategy JSON
          </button>
        </div>
      </div>

      {/* Import Dialog */}
      <dialog className={`modal ${open ? 'modal-open' : ''}`}>
        <div className="modal-box max-w-3xl">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <span className="text-primary"><ImportIcon /></span>
              <h3 className="font-bold text-lg">Import Trendline Strategy</h3>
            </div>
            <button className="btn btn-sm btn-circle btn-ghost" onClick={handleClose}>
              <CloseIcon />
            </button>
          </div>

          {error && (
            <div className="alert alert-error mb-4">
              <span>{error}</span>
            </div>
          )}

          <p className="text-sm text-base-content/60 mb-4">
            Paste your strategy JSON below. The form will be populated with the imported data.
          </p>

          <div className="flex gap-2 mb-4">
            <button className="btn btn-outline btn-sm gap-1" onClick={handlePaste}>
              <PasteIcon /> Paste from Clipboard
            </button>
            <button className="btn btn-outline btn-error btn-sm gap-1" onClick={handleClear}>
              <ClearIcon /> Clear
            </button>
          </div>

          <textarea
            className="textarea textarea-bordered w-full font-mono text-sm h-64"
            value={jsonInput}
            onChange={handleInputChange}
            placeholder="Paste your strategy JSON here..."
          />

          {/* Example JSON Collapse */}
          <div className="collapse collapse-arrow border border-base-300 bg-base-100 rounded-lg mt-4">
            <input type="checkbox" />
            <div className="collapse-title flex items-center gap-2 text-sm">
              <CodeIcon />
              <span>View Example JSON Format</span>
            </div>
            <div className="collapse-content">
              <pre className="p-2 bg-base-200 rounded text-xs overflow-auto max-h-72">
                {exampleJson}
              </pre>
              <button className="btn btn-sm btn-ghost mt-2" onClick={() => setJsonInput(exampleJson)}>
                Use Example
              </button>
            </div>
          </div>

          <div className="modal-action">
            <button className="btn btn-ghost" onClick={handleClose}>Cancel</button>
            <button
              className="btn btn-primary gap-1"
              onClick={handleImport}
              disabled={!jsonInput}
            >
              <ImportIcon />
              Import & Populate Form
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={handleClose}>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default TrendlineStrategyImporter;
